import { useQuery } from '@tanstack/react-query';
import { bliksyKeys } from '../query-keys';
import { apiClient } from '../client';
import {
  loadBliksyChatHistoryContract,
  type LoadBliksyChatHistoryResponse,
  type BliksyUIMessage
} from '@bliks-io/contracts';

/**
 * Fetch chat history for a Bliksy session
 *
 * Fetches all user and assistant messages for a session from the backend.
 * Returns raw BliksyUIMessage[] - conversion to UIMessage[] happens in useBliksyChat.
 *
 * @example
 * ```tsx
 * function ChatPanel({ sessionId }: { sessionId: string }) {
 *   const { data: history = [] } = useBliksyChatHistory(sessionId);
 *   // history is BliksyUIMessage[]
 * }
 * ```
 */
export function useBliksyChatHistory(sessionId: string) {
  return useQuery({
    queryKey: bliksyKeys.chatHistory(sessionId),
    queryFn: async ({ signal }): Promise<BliksyUIMessage[]> => {
      const { data } = await apiClient.get<LoadBliksyChatHistoryResponse>(
        loadBliksyChatHistoryContract.path.replace(':sessionId', sessionId),
        { signal }
      );
      return data.messages;
    },
    staleTime: 0 // Always refetch - history may change after each chat interaction
  });
}
